import React, { createContext, useContext, useState } from "react";
import axios from "axios";

const apiBaseUrl = process.env.REACT_APP_API_URL;
const TOKEN_KEY = "auth_token";

interface AuthContextValue {
  isAuthenticated: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function setAuthHeader(token: string | null) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

const storedToken = localStorage.getItem(TOKEN_KEY);
setAuthHeader(storedToken);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(!!storedToken);
  const [error, setError] = useState<string | null>(null);

  async function login(email: string, password: string) {
    try {
      const response = await axios.post(`${apiBaseUrl}/login`, {
        email,
        password,
      });
      const token = response.data.token;
      localStorage.setItem(TOKEN_KEY, token);
      setAuthHeader(token);
      setIsAuthenticated(true);
      setError(null);
    } catch (err) {
      setError("Identifiants invalides");
      throw err;
    }
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setIsAuthenticated(false);
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
